import { useEffect, useRef, useState } from "react";
import {
  RequestCreateBaseRequest,
  RequestDeleteBaseRequest,
  RequestDeleteBaseRequestWrapper,
  RequestGetUserListRequest,
  RequestUpdateBaseRequest,
  ResponseGetAllBasesResponse,
  ResponseGetUserListResonse,
} from "../../services/OpenAPI";
import { loginUserSelector } from "../../recoil/selectors/loginUserSelector";
import { isGeneralUser, isNotOwner, Permission } from "../../constants/roles";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { useApiGetUserList } from "../api/useApiGetUserList";
import { UserListItem } from "../../components/Table/UserListTable";
import { useApiGetBaseSearchOptions } from "../api/useApiGetBaseSearchOptions";
import { BaseListItem } from "../../components/Table/BaseListTable";
import { useApiUpdateBase } from "../api/useApiUpdateBase";
import { baseUpdatedFlagState } from "../../recoil/atoms/updateBaseAtomes";
import { useApiDeleteBase } from "../api/useApiDeleteBase";
import { useApiRegisterBase } from "../api/useApiRegisterBase";
import { baseSearchOptionsAtom } from "../../recoil/atoms/baseSearchOptionsAtoms";

export type SearchGenreOption = {
  id: string;
  label: string;
};

export const useBaseList = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { fetchUserList } = useApiGetUserList();
  const { fetchBaseSearchOptions: fetchBaseList } =
    useApiGetBaseSearchOptions();
  const { registerBase } = useApiRegisterBase();
  const { updateBase } = useApiUpdateBase();
  const { deleteBases } = useApiDeleteBase();
  const { fetchBaseSearchOptions } = useApiGetBaseSearchOptions();
  const loginUser = useRecoilValue(loginUserSelector);
  const setBaseUpdatedFlag = useSetRecoilState(baseUpdatedFlagState);
  const baseUpdatedFlag = useRecoilValue(baseUpdatedFlagState);
  const reserBaseUpdatedFlag = useResetRecoilState(baseUpdatedFlagState);
  const setBaseSearchOptionsState = useSetRecoilState(baseSearchOptionsAtom);

  const [userList, setUserList] = useState<UserListItem[]>([]);
  const [baseList, setBaseList] = useState<BaseListItem[]>([]);
  const [baseUserList, setBaseUserList] = useState<UserListItem[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addBaseDialogOpen, setAddBaseDialogOpen] = useState(false);
  const [baseUsersListDialogOpen, setBaseUsersListDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<BaseListItem | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const handleCloseToast = () => setToastOpen(false);

  const handleCheckChange = (id: string) => {
    setCheckedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleCheckChangeAll = (newCheckedIds: Set<string>) => {
    setCheckedIds(newCheckedIds);
  };

  const transformBaseData = (
    baseList: ResponseGetAllBasesResponse[]
  ): BaseListItem[] => {
    if (!Array.isArray(baseList)) return [];
    const result: BaseListItem[] = [];

    baseList.forEach((base) => {
      result.push({
        id: Number(base.id),
        baseName: base.name,
        address: base.post,
        phoneNum: base.phoneNum,
        mailAddress: base.mail,
        postNum: base.postNum,
      });
    });
    return result;
  };

  const transformUserData = (
    userList: ResponseGetUserListResonse[] | undefined
  ): UserListItem[] => {
    if (!Array.isArray(userList)) return [];
    const result: UserListItem[] = [];

    userList.forEach((user) => {
      if (isGeneralUser(user.permissionId)) {
        const baseIds =
          user.baseInfo?.map((b) => b.id).filter((id): id is number => !!id) ??
          [];

        result.push({
          id: Number(user.id),
          baseIds: baseIds,
          userNumber: user.userNumber,
          lastName: user.lastName,
          firstName: user.firstName,
        });
      }
    });

    return result;
  };

  const handleUpdate = async (updatedBase: BaseListItem) => {
    if (
      !loginUser ||
      !loginUser.tenantId ||
      !loginUser.id ||
      !loginUser.permissionId
    )
      return;

    const request: RequestUpdateBaseRequest = {
      id: Number(updatedBase.id),
      baseName: updatedBase.baseName,
      mailAddress: updatedBase.mailAddress,
      phoneNum: updatedBase.phoneNum,
      postNum: updatedBase.postNum,
      address: updatedBase.address,
      updatedUserId: loginUser.id,
    };
    const result = await updateBase(request);
    if (result) {
      setToastMessage("更新が完了しました");
      setToastSeverity("success");
      setToastOpen(true);
      setBaseUpdatedFlag(true);
    } else {
      setToastMessage("更新に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleRegister = async (baseInfo: BaseListItem) => {
    if (
      !loginUser ||
      !loginUser.tenantId ||
      !loginUser.id ||
      !loginUser.permissionId
    )
      return;

    const request: RequestCreateBaseRequest = {
      baseName: baseInfo.baseName,
      mailAddress: baseInfo.mailAddress,
      phoneNum: baseInfo.phoneNum,
      postNum: baseInfo.postNum,
      address: baseInfo.address,
      createdUserId: loginUser.id,
    };
    const result = await registerBase(request);
    if (result) {
      setToastMessage("登録が完了しました");
      setToastSeverity("success");
      setToastOpen(true);
      setAddBaseDialogOpen(false);
      setBaseUpdatedFlag(true);
    } else {
      setToastMessage("登録に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleRowClick = async (base: BaseListItem) => {
    setSelectedRow(base);
    setEditDialogOpen(true);
  };

  const handleUserListClick = async (base: BaseListItem) => {
    const baseUserList = userList.filter((u) =>
      u.baseIds?.includes(Number(base.id))
    );
    setBaseUserList(baseUserList);
    setBaseUsersListDialogOpen(true);
  };

  const handleCancel = () => {
    setCheckedIds(new Set());
  };

  const handleDelete = async () => {
    if (checkedIds.size === 0) return;

    // 選択されたユーザー情報をまとめる
    const selectedBases = baseList.filter((user) =>
      checkedIds.has(String(user.id))
    );

    // APIのリクエスト形式にマッピング
    const requestPayload: RequestDeleteBaseRequest[] = selectedBases.map(
      (base) => ({
        id: Number(base.id),
        tenantId: loginUser?.tenantId,
        updatedUserId: loginUser?.id,
      })
    );

    const requestPayloadWrapper: RequestDeleteBaseRequestWrapper = {
      req: requestPayload,
    };

    const result = await deleteBases(requestPayloadWrapper);
    if (result) {
      setToastMessage("拠点削除に成功しました");
      setToastSeverity("success");
      setToastOpen(true);
      setCheckedIds(new Set());
      setBaseUpdatedFlag(true);
    } else {
      setToastMessage("拠点削除に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  // 拠点と紐づくユーザー取得
  const fetchData = async (req: RequestGetUserListRequest) => {
    const baseResponse = await fetchBaseList();
    if (baseResponse && baseResponse.length > 0) {
      const transformed = transformBaseData(baseResponse);
      setBaseList(transformed);
    }
    const response = await fetchUserList(req);
    if (response) {
      const transformed = transformUserData(response);
      setUserList(transformed);
    }
    const baseSearchOptions = await fetchBaseSearchOptions();
    let filteredBaseOptions = baseSearchOptions ?? [];

    if (loginUser && isNotOwner(loginUser.permissionId)) {
      const allowedBaseIds = (loginUser.baseInfo ?? []).map((b) => b.id);
      filteredBaseOptions =
        baseSearchOptions?.filter((base) =>
          allowedBaseIds.includes(base.id!)
        ) ?? [];
    }

    setBaseSearchOptionsState({ baseSearchOptions: filteredBaseOptions });
  };

  useEffect(() => {
    if (baseUpdatedFlag) {
      if (loginUser?.tenantId && loginUser?.id) {
        const req: RequestGetUserListRequest = {
          tenantId: loginUser.tenantId,
          baseId: loginUser.baseId!,
          permissionId: Permission.General,
          userId: loginUser.id,
        };
        fetchData(req);
      }
      reserBaseUpdatedFlag();
      setAddBaseDialogOpen(false);
    }
  }, [baseUpdatedFlag]);

  useEffect(() => {
    if (loginUser?.tenantId && loginUser?.id) {
      const req: RequestGetUserListRequest = {
        tenantId: loginUser.tenantId,
        baseId: loginUser.baseId!,
        permissionId: Permission.General,
        userId: loginUser.id,
      };
      fetchData(req);
    }
  }, [loginUser]);

  useEffect(() => {
    if (loginUser?.tenantId && loginUser?.id) {
      const req: RequestGetUserListRequest = {
        tenantId: loginUser.tenantId,
        baseId: loginUser.baseId!,
        permissionId: Permission.General,
        userId: loginUser.id,
      };
      fetchData(req);
    }
  }, []);

  return {
    inputRef,
    baseList,
    baseUserList,
    selectedRow,
    checkedIds,
    handleCheckChange,
    handleUpdate,
    handleRegister,
    handleRowClick,
    handleUserListClick,
    editDialogOpen,
    setEditDialogOpen,
    addBaseDialogOpen,
    baseUsersListDialogOpen,
    setBaseUsersListDialogOpen,
    setAddBaseDialogOpen,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    handleCancel,
    handleDelete,
    handleCheckChangeAll,
  };
};
